import type { PreviewSend } from '../../types/previewSend';
import type { PreviewSendBatch, PreviewSendBatchStatus } from '../../types/previewSendBatch';
import type { PreviewLink } from '../../types/previewLink';
import type {
  ChannelCounters,
  PageViewCounters,
  SendResultsChannel,
  SendResultsData,
  SendResultsRecipient,
  SendResultsStatus,
} from '../../types/sendResults';

// Stage 6A v5 §4.2 — preview adapter. Pure: PreviewSendBatch + its PreviewSend
// list + the referenced PreviewLink list + a precomputed title/audience →
// SendResultsData (source 'preview'). Page-view aggregates come from
// previewLinks.viewCount (v5 §8.3 / Q4 — the ContactPreviewToken summary-page
// view counter is intentionally not unioned in; see ux-debt). Retry actions are
// attached by the route (it owns the mutations).

const STATUS_MAP: Record<PreviewSendBatchStatus, SendResultsStatus> = {
  in_progress: { label: 'In progress', tone: 'progress' },
  completed: { label: 'Completed', tone: 'success' },
  partial: { label: 'Completed with failures', tone: 'warning' },
};

// PreviewSend carries no per-send unsubscribe field (preview unsubscribes flip
// the contact, not the send), so unsubscribed is always 0 here.
function channelCounters(sends: PreviewSend[], channel: SendResultsChannel): ChannelCounters {
  let sent = 0;
  let delivered = 0;
  let bounced = 0;
  let failed = 0;
  let opened = 0;
  let clicked = 0;
  for (const s of sends) {
    if (s.status === 'sent' || s.status === 'delivered' || s.status === 'bounced') sent++;
    if (s.status === 'delivered') delivered++;
    if (s.status === 'bounced') bounced++;
    if (s.status === 'failed') failed++;
    if (s.openCount > 0) opened++;
    if (s.clickCount > 0) clicked++;
  }
  const base: ChannelCounters = { sent, delivered, bounced, failed, unsubscribed: 0 };
  if (channel === 'email') {
    base.opened = opened;
    base.clicked = clicked;
  }
  return base;
}

// Sum viewCount + take the latest lastViewedAt across the links a send covers.
function pageViewsForSend(
  send: PreviewSend,
  linkById: Map<string, PreviewLink>,
): { count: number; lastViewedAt: string | null } {
  let count = 0;
  let lastViewedAt: string | null = null;
  for (const linkId of send.previewLinkIds) {
    const link = linkById.get(linkId);
    if (!link) continue;
    count += link.viewCount;
    if (link.lastViewedAt && (!lastViewedAt || link.lastViewedAt > lastViewedAt)) {
      lastViewedAt = link.lastViewedAt;
    }
  }
  return { count, lastViewedAt };
}

function toRecipient(s: PreviewSend, linkById: Map<string, PreviewLink>): SendResultsRecipient {
  const pv = pageViewsForSend(s, linkById);
  return {
    sendId: s.id,
    contactId: s.contactId,
    contactName: s.contactName,
    to: s.contactEmail ?? s.contactPhone ?? '',
    channel: s.channel,
    status: s.status,
    deliveredAt: s.deliveredAt,
    openedAt: s.openedAt,
    clickCount: s.clickCount,
    firstClickAt: s.firstClickAt,
    // Previews have no per-send unsubscribe record.
    unsubscribedAt: null,
    failureReason: s.failureReason,
    pageViewCount: pv.count,
    lastPageViewAt: pv.lastViewedAt,
  };
}

// Page-view card aggregates are by DISTINCT contact (the card's denominator is
// batch.totalRecipients), summing a contact's view counts across their sends.
function aggregatePageViews(
  recipients: SendResultsRecipient[],
  totalRecipients: number,
): PageViewCounters {
  const perContact = new Map<string, number>();
  let totalViews = 0;
  for (const r of recipients) {
    const v = r.pageViewCount ?? 0;
    totalViews += v;
    perContact.set(r.contactId, (perContact.get(r.contactId) ?? 0) + v);
  }
  let withViews = 0;
  let withMultiple = 0;
  for (const v of perContact.values()) {
    if (v >= 1) withViews++;
    if (v > 1) withMultiple++;
  }
  return {
    totalViews,
    recipientsWithViews: withViews,
    recipientsWithoutViews: Math.max(0, totalRecipients - withViews),
    recipientsWithMultipleViews: withMultiple,
  };
}

export function previewSendBatchToResultsData(
  batch: PreviewSendBatch,
  sends: PreviewSend[],
  links: PreviewLink[],
  opts: { title: string; audienceDescription: string },
): SendResultsData {
  const linkById = new Map(links.map((l) => [l.id, l]));
  const recipients = sends.map((s) => toRecipient(s, linkById));

  const emailSends = sends.filter((s) => s.channel === 'email');
  const smsSends = sends.filter((s) => s.channel === 'sms');
  const counters: SendResultsData['counters'] = {};
  if (batch.channels.includes('email')) counters.email = channelCounters(emailSends, 'email');
  if (batch.channels.includes('sms')) counters.sms = channelCounters(smsSends, 'sms');

  return {
    id: batch.id,
    title: opts.title,
    status: STATUS_MAP[batch.status],
    sentAt: batch.initiatedAt,
    source: 'preview',
    isInFlight: batch.status === 'in_progress',
    audienceDescription: opts.audienceDescription,
    channels: batch.channels,
    fromMode: null,
    counters,
    pageViews: aggregatePageViews(recipients, batch.totalRecipients),
    // Previews have no stored AM template (v5 Q2) — empty ⇒ panel hidden.
    messagePreview: {},
    recipients,
    actions: {},
  };
}
